/**
 * Session-backed wholesale order cart (not Shopify cart).
 */
(function () {
  const STORAGE_KEY = 'akWholesaleOrderCart';

  function randomId() {
    return 'ak-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9);
  }

  const SIZE_ORDER = ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

  function formatQuantitiesText(quantities) {
    if (!quantities || typeof quantities !== 'object') return '';

    const keys = Object.keys(quantities);
    const extras = keys.filter((size) => SIZE_ORDER.indexOf(size) === -1);

    return SIZE_ORDER.concat(extras)
      .map((size) => {
        const quantity = parseInt(quantities[size], 10);
        if (!quantity || Number.isNaN(quantity)) return null;
        return size + ': ' + quantity;
      })
      .filter(Boolean)
      .join(', ');
  }

  function lineKey(line) {
    return [line.productHandle || '', line.sku || '', line.color || ''].join('|');
  }

  function mergeQuantities(a, b) {
    const merged = { ...a };

    Object.keys(b || {}).forEach((size) => {
      const next = parseInt(b[size], 10) || 0;
      if (!next) return;
      const prev = parseInt(merged[size], 10) || 0;
      merged[size] = String(prev + next);
    });

    return merged;
  }

  function normalizeQuantities(quantities) {
    const normalized = {};

    Object.keys(quantities || {}).forEach((size) => {
      const sizeKey = String(size).trim().toUpperCase();
      if (!sizeKey) return;
      const quantity = parseInt(quantities[size], 10);
      if (!quantity || Number.isNaN(quantity)) return;
      normalized[sizeKey] = String(quantity);
    });

    return normalized;
  }

  function dispatchUpdated(lines) {
    document.dispatchEvent(
      new CustomEvent('ak-wholesale-cart:updated', {
        bubbles: true,
        detail: {
          lines: lines,
          count: lines.length
        }
      })
    );
  }

  window.AkWholesaleCart = {
    load() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    },

    save(lines) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
      dispatchUpdated(lines);
    },

    getLines() {
      return this.load();
    },

    getCount() {
      return this.load().length;
    },

    clear() {
      this.save([]);
    },

    async resolveLineTitles() {
      if (!window.AkWholesaleImages) return;

      const lines = this.load();
      let changed = false;

      for (const line of lines) {
        if (!line.productHandle) continue;
        const product = await window.AkWholesaleImages.fetchProductJson(line.productHandle);
        if (!product?.title) continue;
        if (line.productTitle !== product.title) {
          line.productTitle = product.title;
          changed = true;
        }
      }

      if (changed) this.save(lines);
    },

    removeLine(lineId) {
      const lines = this.load().filter((line) => line.lineId !== lineId);
      this.save(lines);
      return lines;
    },

    updateLineImage(lineId, imageUrl) {
      this.updateLineFields(lineId, { imageUrl: imageUrl });
    },

    updateLineFields(lineId, fields) {
      if (!lineId || !fields) return;

      const lines = this.load();
      const index = lines.findIndex((line) => line.lineId === lineId);
      if (index < 0) return;

      if (fields.imageUrl != null) {
        lines[index].imageUrl = window.AkWholesaleImages?.normalizeUrl(fields.imageUrl) || '';
      }
      if (fields.productTitle != null && String(fields.productTitle).trim()) {
        lines[index].productTitle = String(fields.productTitle).trim();
      }

      this.save(lines);
    },

    updateLineQuantities(lineId, quantities) {
      if (!lineId) return;

      const normalized = normalizeQuantities(quantities);
      const lines = this.load();
      const index = lines.findIndex((line) => line.lineId === lineId);
      if (index < 0) return;

      if (!formatQuantitiesText(normalized)) {
        lines.splice(index, 1);
      } else {
        lines[index].quantities = normalized;
        lines[index].quantitiesText = formatQuantitiesText(normalized);
      }

      this.save(lines);
      return lines;
    },

    addLine(payload) {
      const quantities = normalizeQuantities(payload.quantities);
      const line = {
        lineId: randomId(),
        productHandle: payload.productHandle || '',
        productTitle: payload.productTitle || '',
        variantId: payload.variantId || '',
        sku: payload.sku || '',
        color: payload.color || '',
        quantities: quantities,
        quantitiesText: formatQuantitiesText(quantities),
        imageUrl: window.AkWholesaleImages?.normalizeUrl(payload.imageUrl) || ''
      };

      if (!line.quantitiesText) {
        return { ok: false, error: 'zero' };
      }

      const lines = this.load();
      const key = lineKey(line);
      let existingIndex = -1;

      if (line.productHandle && line.variantId) {
        existingIndex = lines.findIndex(
          (item) => item.productHandle === line.productHandle && String(item.variantId) === String(line.variantId)
        );
      }

      if (existingIndex < 0) {
        existingIndex = lines.findIndex((item) => lineKey(item) === key);
      }

      const replaceQuantities = payload.replaceQuantities === true;

      if (existingIndex >= 0) {
        const existing = lines[existingIndex];
        existing.quantities = replaceQuantities
          ? quantities
          : mergeQuantities(existing.quantities, quantities);
        existing.quantitiesText = formatQuantitiesText(existing.quantities);
        existing.variantId = line.variantId || existing.variantId;
        existing.sku = line.sku || existing.sku;
        existing.color = line.color || '';
        if (line.productTitle) existing.productTitle = line.productTitle;
        if (line.imageUrl) existing.imageUrl = line.imageUrl;
        lines[existingIndex] = existing;
      } else {
        lines.push(line);
      }

      this.save(lines);
      return { ok: true, lines: lines };
    },

    formatOrderLinesForContact(lines) {
      return (lines || this.load())
        .map((line) => {
          const sku = (line.sku || '').trim();
          const quantitiesText = (line.quantitiesText || '').trim();
          if (!quantitiesText) return sku;
          return sku + ' | ' + quantitiesText;
        })
        .join('\n');
    },

    getTotalUnits(lines) {
      return (lines || this.load()).reduce((total, line) => {
        const quantities = line.quantities || {};
        return total + Object.keys(quantities).reduce((lineTotal, size) => {
          const quantity = parseInt(quantities[size], 10);
          return lineTotal + (Number.isNaN(quantity) ? 0 : quantity);
        }, 0);
      }, 0);
    },

    syncDrawerToggle(expanded) {
      document.querySelectorAll('[data-action="toggle-ak-wholesale-cart"]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        const cartIcon = toggle.querySelector('.header__cart-icon');
        if (cartIcon) {
          cartIcon.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        }
      });
    },

    setPanelHeight(drawer) {
      const panel = drawer?.querySelector('.collection-drawer');
      if (panel) {
        panel.style.maxHeight = `${window.innerHeight}px`;
      }
    },

    openDrawer() {
      const drawer = document.getElementById('ak-wholesale-order-drawer');
      if (!drawer) return;

      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('is-locked');
      this.syncDrawerToggle(true);
      this.setPanelHeight(drawer);

      const open = () => {
        drawer.dispatchEvent(new CustomEvent('ak-wholesale-cart:open', { bubbles: true }));
      };

      if (this.resolveLineTitles) {
        this.resolveLineTitles().then(open).catch(open);
      } else {
        open();
      }
    },

    closeDrawer() {
      const drawer = document.getElementById('ak-wholesale-order-drawer');
      if (!drawer) return;

      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('is-locked');
      this.syncDrawerToggle(false);
      drawer.dispatchEvent(new CustomEvent('ak-wholesale-cart:close', { bubbles: true }));
    }
  };

  function syncHeaderBadge() {
    const count = window.AkWholesaleCart?.getCount() || 0;
    document.querySelectorAll('[data-ak-wholesale-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  document.addEventListener('DOMContentLoaded', syncHeaderBadge);
  document.addEventListener('ak-wholesale-cart:updated', syncHeaderBadge);

  const productJsonCache = {};

  window.AkWholesaleImages = {
    normalizeUrl(url) {
      if (url == null) return '';
      const value = typeof url === 'string' ? url.trim() : '';
      if (!value) return '';
      if (value.startsWith('//')) return 'https:' + value;
      return value;
    },

    urlFromVariant(variant, fallbackUrl) {
      if (variant?.featured_image) {
        if (typeof variant.featured_image === 'string') {
          return this.normalizeUrl(variant.featured_image);
        }
        if (variant.featured_image.src) {
          return this.normalizeUrl(variant.featured_image.src);
        }
      }

      if (variant?.featured_media?.preview_image?.src) {
        return this.normalizeUrl(variant.featured_media.preview_image.src);
      }

      return this.normalizeUrl(fallbackUrl);
    },

    readVariantFromForm(productFormId) {
      if (!productFormId) return null;
      const picker = document.querySelector(`variant-picker[form-id="${productFormId}"]`);
      const script = picker?.querySelector('script[data-variant]');
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    },

    productJsonUrl(handle) {
      const root = window.Shopify?.routes?.root || window.routes?.rootUrl || '/';
      const base = root.endsWith('/') ? root : `${root}/`;
      return `${base}products/${encodeURIComponent(handle)}.js`;
    },

    async fetchProductJson(handle) {
      if (!handle) return null;
      if (productJsonCache[handle]) return productJsonCache[handle];

      try {
        const response = await fetch(this.productJsonUrl(handle), {
          method: 'GET',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return null;
        const product = await response.json();
        productJsonCache[handle] = product;
        return product;
      } catch (error) {
        return null;
      }
    },

    imageFromProductJson(product, variantId) {
      if (!product) return '';

      let url = product.featured_image;
      if (variantId && Array.isArray(product.variants)) {
        const variant = product.variants.find((item) => String(item.id) === String(variantId));
        if (variant?.featured_image) {
          url = typeof variant.featured_image === 'string'
            ? variant.featured_image
            : variant.featured_image.src || url;
        }
      }

      return this.normalizeUrl(url);
    },

    async resolveLineImage(line) {
      const stored = this.normalizeUrl(line?.imageUrl);
      if (stored) return stored;

      const product = await this.fetchProductJson(line?.productHandle);
      return this.imageFromProductJson(product, line?.variantId);
    }
  };
})();
